"use client";

import { useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { InteractionPlot } from "@/components/tools/stats-tests/interaction-plot";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import type { TWO_WAY_ANOVA_CALCULATOR_STRINGS } from "@/components/tools/two-way-anova-calculator";
import { t } from "@/lib/i18n/interpolate";
import type { CitationParts } from "@/lib/tool-kit/citation-parts";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type {
  EffectRow,
  TwoWayAnova,
} from "@/lib/tool-kit/stats-tests/two-way-anova";

/** Results for the two-way ANOVA calculator. */

type Strings = typeof TWO_WAY_ANOVA_CALCULATOR_STRINGS;

function CellMeans({
  r,
  factorA,
  strings,
  fmt,
}: {
  r: TwoWayAnova;
  factorA: string;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt, 2);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.cellHeading}</h3>
      <p className={HINT}>{strings.cellHint}</p>
      <ResultTable
        head={[factorA, ...r.levelsB, strings.marginal]}
        rows={[
          ...r.cells.map((row, i) => ({
            key: `a-${r.levelsA[i]}`,
            cells: [
              r.levelsA[i],
              ...row.map((cell) =>
                t(strings.cellValue, {
                  mean: n(cell.mean),
                  sd: Number.isNaN(cell.sd) ? "—" : n(cell.sd),
                  n: fmt(cell.n),
                })
              ),
              n(r.marginalA[i]),
            ],
          })),
          {
            key: "marginal",
            cells: [strings.marginal, ...r.marginalB.map(n), n(r.grandMean)],
          },
        ]}
      />
    </>
  );
}

export function TwoWayResults({
  r,
  factorA,
  factorB,
  alpha,
  strings,
  fmt,
}: {
  r: TwoWayAnova;
  factorA: string;
  factorB: string;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [swap, setSwap] = useState(false);
  const n = (v: number) => formatStat(v, fmt);
  const names: Record<EffectRow["key"], string> = {
    a: factorA,
    b: factorB,
    ab: t(strings.interaction, { a: factorA, b: factorB }),
  };
  const apa: CitationParts = r.effects.flatMap((effect, i) => [
    ...(i > 0 ? [{ text: "; " }] : []),
    ...richTemplate(strings.apaTemplate, {
      effect: names[effect.key],
      df1: fmt(effect.df),
      df2: fmt(r.dfError),
      f: fixed(effect.f, 2, fmt),
      p: apaP(effect.p, fmt),
      eta: fixed(effect.partialEtaSquared, 2, fmt, false),
    }),
  ]);
  const interaction = r.effects[2];
  const cellMeans = r.cells.map((row) => row.map((cell) => cell.mean));
  const transposed = r.levelsB.map((_, j) => cellMeans.map((row) => row[j]));
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <p className={HINT}>
        {r.balanced
          ? t(strings.balanced, { n: fmt(r.cells[0][0].n) })
          : strings.unbalanced}
      </p>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <ResultTable
        head={[
          strings.source,
          strings.ss,
          strings.df,
          strings.ms,
          strings.f,
          strings.p,
          strings.partialEta,
        ]}
        rows={[
          ...r.effects.map((effect) => ({
            key: effect.key,
            cells: [
              names[effect.key],
              n(effect.ss),
              fmt(effect.df),
              n(effect.ms),
              n(effect.f),
              formatP(effect.p, fmt),
              n(effect.partialEtaSquared),
            ],
          })),
          {
            key: "error",
            cells: [
              strings.error,
              n(r.ssError),
              fmt(r.dfError),
              n(r.msError),
              "",
              "",
              "",
            ],
          },
          {
            key: "total",
            cells: [
              strings.total,
              n(r.ssTotal),
              fmt(r.dfTotal),
              "",
              "",
              "",
              "",
            ],
          },
        ]}
      />
      <div className="space-y-2">
        {r.effects.map((effect) => (
          <Decision key={effect.key} reject={effect.p < alpha}>
            {t(
              effect.p < alpha ? strings.decisionReject : strings.decisionKeep,
              {
                effect: names[effect.key],
                alpha: fmt(alpha),
                p: formatP(effect.p, fmt),
              }
            )}
          </Decision>
        ))}
      </div>
      {interaction.p < alpha ? (
        <p className={HINT}>{strings.interactionNote}</p>
      ) : null}
      <CellMeans factorA={factorA} fmt={fmt} r={r} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.plotHeading}</h3>
      <p className={HINT}>{strings.plotHint}</p>
      <Checkbox
        checked={swap}
        label={t(strings.swapAxes, { a: factorA })}
        onChange={setSwap}
      />
      <InteractionPlot
        downloadLabel={strings.downloadPlot}
        fmt={fmt}
        label={t(strings.plotLabel, {
          trace: swap ? factorB : factorA,
          x: swap ? factorA : factorB,
        })}
        means={swap ? transposed : cellMeans}
        traceLevels={swap ? r.levelsB : r.levelsA}
        xLevels={swap ? r.levelsA : r.levelsB}
        xTitle={swap ? factorA : factorB}
        yTitle={strings.yTitle}
      />
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingError, {
            ss: n(r.ssError),
            n: fmt(r.n),
            cells: fmt(r.levelsA.length * r.levelsB.length),
            df: fmt(r.dfError),
            ms: n(r.msError),
          })}
        </p>
        <p>{strings.workingMethod}</p>
        {r.effects.map((effect) => (
          <p key={effect.key}>
            {t(strings.workingEffect, {
              effect: names[effect.key],
              ss: n(effect.ss),
              df: fmt(effect.df),
              ms: n(r.msError),
              f: n(effect.f),
              eta: n(effect.partialEtaSquared),
            })}
          </p>
        ))}
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        parts={apa}
      />
    </section>
  );
}
